import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PdfExtractionService } from '@app/_services/pdf-extraction.service';
import { PdfExtractionApiService } from '@app/_services/pdf-extraction-api.service';
import { FormPopulationService } from '@app/_services/form-population.service';
import { ExtractedData, PdfExtractionResult } from '@app/_models/extracted-data.model';
import { FieldMapping, FormPopulationSettings } from '@app/_models/field-mapping.model';
import { UntypedFormGroup } from '@angular/forms';
import { AccountService } from '@app/_services/account.service';
import { ConfigDataService } from '@app/_services/config-data.service';
import { MakeModel } from '@app/_models/makeModel';
import { User } from '@app/_models';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-pdf-upload-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pdf-upload-panel.component.html',
  styleUrls: ['./pdf-upload-panel.component.less']
})
export class PdfUploadPanelComponent implements OnInit {
  @Input() targetForm!: UntypedFormGroup;
  @Output() dataExtracted = new EventEmitter<ExtractedData>();
  @Output() formPopulated = new EventEmitter<{populated: string[], skipped: string[], conflicts: string[]}>();

  // User access control
  user: User;
  hasUploadPermission = false;

  // State management
  isProcessing = false;
  isDragOver = false;
  uploadProgress = 0;
  
  // Data
  selectedFile: File | null = null;
  extractionResult: PdfExtractionResult | null = null;
  extractedData: ExtractedData | null = null;
  fieldMappings: FieldMapping[] = [];
  showDataPreview = false;
  
  // Settings
  populationSettings: FormPopulationSettings = {
    mappings: [],
    conflictStrategy: 'ask',
    minimumConfidence: 60,
    preserveExistingData: true
  };

  // Extraction mode tracking
  extractionMode: 'client' | 'backend' | 'hybrid' = 'hybrid';
  useBackendExtraction = true;
  backendError: string | null = null;
  fallbackUsed = false;

  constructor(
    private pdfExtractionService: PdfExtractionService,
    private pdfExtractionApiService: PdfExtractionApiService,
    private formPopulationService: FormPopulationService,
    private accountService: AccountService,
    private configDataService: ConfigDataService
  ) {
    this.user = this.accountService.userValue;
    this.hasUploadPermission = this.user && 
                               this.user.specialRoleAccess && 
                               this.user.specialRoleAccess.includes('AllowHeroPolicyPDFUpload');
  }

  ngOnInit(): void {
    // Check permission on init
    if (!this.hasUploadPermission) {
      console.warn('[PDF Upload] User does not have AllowHeroPolicyPDFUpload permission');
    }
  }

  /**
   * Handle file drop
   */
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  /**
   * Handle file input change
   */
  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  /**
   * Handle drag over
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  /**
   * Handle drag leave
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  /**
   * Handle file selection
   */
  private handleFileSelection(file: File): void {
    // Validate file
    if (!this.validateFile(file)) {
      return;
    }

    this.selectedFile = file;
    this.extractDataFromPdf();
  }

  /**
   * Validate uploaded file
   */
  private validateFile(file: File): boolean {
    // Check file type
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file only.');
      return false;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 10MB.');
      return false;
    }

    return true;
  }

  /**
   * Extract data from PDF
   */
  private extractDataFromPdf(): void {
    if (!this.selectedFile) return;

    this.isProcessing = true;
    this.uploadProgress = 0;
    this.backendError = null;
    this.fallbackUsed = false;
    
    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
      }
    }, 200);

    // Try hybrid extraction: backend first, then fallback to client
    if (this.useBackendExtraction && this.extractionMode !== 'client') {
      this.extractWithBackend(progressInterval);
    } else {
      this.extractWithClient(progressInterval);
    }
  }

  /**
   * Extract data using backend API
   */
  private extractWithBackend(progressInterval: any): void {
    console.log('[PDF Upload] Attempting backend extraction...');
    
      this.pdfExtractionApiService.extractDataFromPdf(this.selectedFile!).subscribe({
      next: async (backendResponse) => {
        clearInterval(progressInterval);
        this.uploadProgress = 100;
        
        console.log('[PDF Upload] Backend extraction successful', backendResponse);
        
          if (backendResponse.success && backendResponse.data) {
          // Convert backend response to ExtractedData format (async to handle model extraction)
          const extractedData = await this.convertBackendResponseToExtractedData(backendResponse.data);
          this.extractionResult = {
            success: true,
            extractedData: extractedData,
            errors: [],
            warnings: []
          };
          
          this.extractedData = extractedData;
          this.generateFieldMappings();
          // Auto-populate immediately without showing preview
          this.dataExtracted.emit(extractedData);
          this.extractionMode = 'backend';
        } else {
          this.backendError = backendResponse.error || 'Backend extraction failed';
          console.warn('[PDF Upload] Backend extraction failed, falling back to client-side', this.backendError);
          this.extractWithClientFallback(progressInterval);
        }
        
        setTimeout(() => {
          this.isProcessing = false;
        }, 500);
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.backendError = error.message || 'Backend extraction failed';
        console.error('[PDF Upload] Backend extraction error:', error);
        
        if (this.extractionMode === 'hybrid') {
          console.log('[PDF Upload] Falling back to client-side extraction...');
          this.extractWithClientFallback(setInterval(() => {
            if (this.uploadProgress < 90) {
              this.uploadProgress += 10;
            }
          }, 200));
        } else {
          this.isProcessing = false;
          alert(`Extraction failed: ${this.backendError}`);
        }
      }
    });
  }

  /**
   * Extract data using client-side service with fallback notification
   */
  private extractWithClientFallback(progressInterval: any): void {
    this.fallbackUsed = true;
    this.extractWithClient(progressInterval);
  }

  /**
   * Extract data using client-side service
   */
  private extractWithClient(progressInterval: any): void {
    console.log('[PDF Upload] Attempting client-side extraction...');
    
    this.pdfExtractionService.extractDataFromPdf(this.selectedFile!).subscribe({
      next: (result: PdfExtractionResult) => {
        clearInterval(progressInterval);
        this.uploadProgress = 100;
        
        console.log('[PDF Upload] Client-side extraction successful', result);
        
        this.extractionResult = result;
        
        if (result.success && result.extractedData) {
          this.extractedData = result.extractedData;
          this.generateFieldMappings();
          // Auto-populate immediately without showing preview
          this.dataExtracted.emit(result.extractedData);
          
          if (this.extractionMode !== 'backend') {
            this.extractionMode = 'client';
          }
        } else {
          alert(`Extraction failed: ${result.errors?.join(', ') || 'Unknown error'}`);
        }
        
        setTimeout(() => {
          this.isProcessing = false;
        }, 500);
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.isProcessing = false;
        console.error('[PDF Upload] Client-side extraction error:', error);
        
        let errorMessage = 'Failed to extract data from PDF. ';
        if (this.backendError) {
          errorMessage += `Backend: ${this.backendError}. Client: ${error.message}`;
        } else {
          errorMessage += error.message || 'Please try again.';
        }
        
        alert(errorMessage);
      }
    });
  }

  /**
   * Generate field mappings
   */
  private generateFieldMappings(): void {
    if (!this.extractedData) return;

    this.fieldMappings = this.formPopulationService.generateAutomaticMappings(this.extractedData);
    this.populationSettings.mappings = this.fieldMappings;
  }

  /**
   * Convert backend API response to ExtractedData format
   */
  private async convertBackendResponseToExtractedData(backendData: any): Promise<ExtractedData> {
    const startTime = Date.now();
    
    // Create ExtractedData object from backend response
    const extractedData: ExtractedData = {
      extractionMetadata: {
        fileName: this.selectedFile?.name || 'unknown.pdf',
        fileSize: this.selectedFile?.size || 0,
        pageCount: 1,
        extractionTime: new Date(),
        totalConfidence: 85, // Backend extraction is more reliable
        extractedFieldsCount: Object.keys(backendData).length,
        processingTimeMs: Date.now() - startTime
      }
    };

    // Map backend response fields to ExtractedData fields
    const fieldMapping: { [key: string]: string } = {
      'insured': 'firstName',
      'addressOfInsured': 'address',
      'vehicleRegistrationNo': 'registrationNumber',
      'engineNo': 'engineNumber',
      'chassisNo': 'chasisNumber',
      'makeModel': 'vehicleMake', // Will be processed separately
      'yearOfMfg': 'vehicleYear',
      'nomineeName': 'nomineeName',
      'nomineeAge': 'nomineeAge',
      'nomineeRelation': 'nomineeRelationship'
    };

    // Convert each field
    for (const backendField of Object.keys(backendData)) {
      const extractedField = fieldMapping[backendField] || backendField;
      const value = backendData[backendField];
      
      // Special handling for makeModel field - wait for async processing
      if (backendField === 'makeModel' && value) {
        await this.processMakeModelField(extractedData, value.toString());
      } else if (value) {
        (extractedData as any)[extractedField] = {
          fieldName: extractedField,
          value: value.toString(),
          confidence: 90, // Backend extraction has high confidence
          source: 'backend'
        };
      }
    }

    return extractedData;
  }

  /**
   * Extract model from makeModel field by searching config models
   * @param extractedData The ExtractedData object to populate
   * @param makeModelValue The makeModel value from backend (e.g., "Honda Motorcycle Scooter India Pvt. Ltd. SP125 DLX DISK")
   */
  private async processMakeModelField(extractedData: ExtractedData, makeModelValue: string): Promise<void> {
    console.log('[PDF Upload] Processing makeModel field:', makeModelValue);
    
    // Helper function to escape regex
    const escapeRegex = (str: string): string => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    
    return new Promise<void>((resolve) => {
      // Get all models from config service
      this.configDataService.MakeModel$.pipe(take(1)).subscribe((makeModels: MakeModel[]) => {
      if (!makeModels || makeModels.length === 0) {
        console.warn('[PDF Upload] No make models available in config, cannot extract model');
        // Don't set anything - just return
        resolve();
        return;
      }

      // Collect all model names from all makes
      const allModelNames: string[] = [];
      makeModels.forEach(makeModel => {
        if (makeModel.model && makeModel.model.length > 0) {
          allModelNames.push(...makeModel.model);
        }
      });

      console.log('[PDF Upload] Searching through', allModelNames.length, 'models from config');

      // Search for the first word of each model in the makeModel string
      let extractedModel: string | null = null;
      let matchedModelWord: string | null = null;

      for (const modelName of allModelNames) {
        if (!modelName || !modelName.trim()) continue;
        
        // Get the first word of the model (e.g., "SP125" from "SP125 DLX DISK")
        const firstWord = modelName.trim().split(/\s+/)[0];
        if (!firstWord) continue;

        // Search for this word in the makeModel string (case-insensitive)
        const regex = new RegExp(`\\b${escapeRegex(firstWord)}\\b`, 'i');
        const matchIndex = makeModelValue.search(regex);
        
        if (matchIndex !== -1) {
          // Found the model word - extract everything from that word onwards
          extractedModel = makeModelValue.substring(matchIndex).trim();
          matchedModelWord = firstWord;
          console.log('[PDF Upload] Found model match:', {
            modelWord: firstWord,
            fullModel: modelName,
            extractedModel: extractedModel
          });
          break;
        }
      }

      if (extractedModel) {
        // Set model field only (don't touch vehicleMake)
        (extractedData as any).model = {
          fieldName: 'model',
          value: extractedModel,
          confidence: 90,
          source: 'backend'
        };

        console.log('[PDF Upload] Extracted model:', extractedModel);
        resolve();
      } else {
        // No model match found - don't set anything
        console.warn('[PDF Upload] No model match found in makeModel value');
        resolve();
      }
    });
    });
  }

  /**
   * Escape special regex characters in a string
   */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Toggle field mapping selection
   */
  toggleFieldMapping(mapping: FieldMapping): void {
    mapping.isSelected = !mapping.isSelected;
  }

  /**
   * Change conflict resolution for a mapping
   */
  changeConflictResolution(mapping: FieldMapping, resolution: 'overwrite' | 'skip' | 'merge'): void {
    mapping.conflictResolution = resolution;
  }

  /**
   * Populate form with extracted data
   */
  populateForm(): void {
    if (!this.extractedData || !this.targetForm) {
      alert('No data available to populate the form.');
      return;
    }

    const result = this.formPopulationService.populateForm(
      this.targetForm,
      this.extractedData,
      this.populationSettings
    );

    this.formPopulated.emit(result);

    // Show results to user
    let message = `Form population completed!\n\n`;
    message += `✅ Populated: ${result.populated.length} fields\n`;
    message += `⏭️ Skipped: ${result.skipped.length} fields\n`;
    message += `⚠️ Conflicts: ${result.conflicts.length} fields`;

    if (result.populated.length > 0) {
      message += `\n\nPopulated fields: ${result.populated.join(', ')}`;
    }

    alert(message);
  }

  /**
   * Clear uploaded file and reset state
   */
  clearFile(): void {
    this.selectedFile = null;
    this.extractionResult = null;
    this.extractedData = null;
    this.fieldMappings = [];
    this.showDataPreview = false;
    this.isProcessing = false;
    this.uploadProgress = 0;
  }

  /**
   * Get confidence color class
   */
  getConfidenceColorClass(confidence: number): string {
    if (confidence >= 80) return 'text-success';
    if (confidence >= 60) return 'text-warning';
    return 'text-danger';
  }

  /**
   * Get confidence label
   */
  getConfidenceLabel(confidence: number): string {
    if (confidence >= 80) return 'High';
    if (confidence >= 60) return 'Medium';
    return 'Low';
  }

  // Helper methods for template
  areAllSelected(): boolean {
    return this.fieldMappings.every(m => m.isSelected);
  }

  toggleSelectAll(event: any): void {
    const checked = event.target.checked;
    this.fieldMappings.forEach(m => m.isSelected = checked);
  }

  getSelectedFieldsCount(): number {
    return this.fieldMappings.filter(m => m.isSelected).length;
  }

  hasSelectedFields(): boolean {
    return this.fieldMappings.some(m => m.isSelected);
  }

  getExtractedValue(mapping: FieldMapping): string {
    const data = this.extractedData as any;
    return data[mapping.extractedFieldName]?.value || '';
  }
}